import { List, Map } from 'immutable';

import { isTruthy } from '../bool';
import { getSize } from '../iterable/getSize';
import { reduce } from '../iterable/reduce';
import { get } from '../iterable/get';

import { ALL, COMBINATIONS, DELETED, INFLUENCE, VARIANTS } from '../../constants/variations';
import { AMOUNT, ATTRIBUTE_DEFAULTS, VALUE, WEIGHT_UNITS } from '../../constants/product';
import { ETSY, SHOPIFY } from '../../constants/channels';
import { EMPTY, VELA } from '../../constants';
import ATTRIBUTE from '../../constants/attributes';

function getCombinationPath(combination) {
  return combination.get('options').map(get('name')).toArray();
}

export function isCombinationDeleted({ combination, deleted }) {
  function reduceOptions(result, option) {
    if (result.included) return result;

    result.path.push(option.get('name'));
    result.included = deleted.getIn(result.path) === true;

    return result;
  }

  return combination.get('options').reduce(reduceOptions, { included: false, path: [] }).included;
}

export function isOptionDeleted({ deleted, depth = 0, option, variants, variantIndex }) {
  if (depth === variantIndex) {
    if (deleted === true) {
      return true;
    }

    if (Map.isMap(deleted) && deleted.get(option.get('value')) === true) {
      return true;
    }

    return false;
  }

  function reduceDeleted(result, combination) {
    return (
      result &&
      isOptionDeleted({ deleted: combination, depth: depth + 1, option, variants, variantIndex })
    );
  }

  if (depth < variantIndex) {
    const options = variants.getIn([depth, 'options']);

    if (Map.isMap(deleted) && getSize(deleted) < getSize(options)) {
      return false;
    }

    return deleted.reduce(reduceDeleted, true);
  }

  return false;
}

export function isVariantDeleted({ deleted, depth = 0, variants }) {
  if (deleted === true) {
    return true;
  }

  if (!Map.isMap(deleted)) {
    return false;
  }

  const options = variants.getIn([depth, 'options']);

  if (getSize(deleted) < getSize(options)) {
    return false;
  }

  function reduceDeleted(result, combination) {
    return (
      isVariantDeleted({ deleted: combination, depth: depth + 1, variants }) &&
      result
    );
  }

  if (depth < getSize(variants)) {
    return deleted.reduce(reduceDeleted, true);
  }

  return false;
}

function addCombinationToDeleted({ combination, deleted }) {
  const path = getCombinationPath(combination);
  return deleted.setIn(path, true);
}

function removeCombinationFromDeleted({ combination, deleted: source }) {
  const path = getCombinationPath(combination);
  let deleted = source.deleteIn(path);

  path.pop();

  while (getSize(path) > 0) {
    if (getSize(deleted.getIn(path)) === 0) {
      deleted = deleted.deleteIn(path);
    }

    path.pop();
  }

  return deleted;
}

export function getDeletedCombinations({ variations }) {
  const variants = variations.get(VARIANTS);
  const combinations = variations.get(COMBINATIONS);

  if (getSize(variants) === 0) return EMPTY.MAP;

  function getCombinationsMap(depth) {
    function reduceOptions(result, option) {
      const combination = depth === getSize(variants) - 1
        ? true
        : getCombinationsMap(depth + 1);

      return result.set(option.get('value'), combination);
    }

    return variants.getIn([depth, 'options']).reduce(reduceOptions, EMPTY.MAP);
  }

  function reduceCombinations(deleted, combination) {
    if (isCombinationDeleted({ combination, deleted })) {
      if (!combination.get(DELETED)) {
        return removeCombinationFromDeleted({ combination, deleted });
      }
    } else if (combination.get(DELETED)) {
      return addCombinationToDeleted({ combination, deleted });
    }

    return deleted;
  }

  function reduceDeleted(deleted, depth) {
    if (!Map.isMap(deleted) || getSize(deleted) === 0) {
      return deleted;
    }

    if (depth === getSize(variants) && getSize(deleted) === getSize(variants.getIn([depth, 'options']))) {
      return true;
    }

    function mapDeleted(items) {
      return reduceDeleted(items, depth + 1);
    }

    return deleted.map(mapDeleted);
  }

  return reduceDeleted(combinations.reduce(reduceCombinations, getCombinationsMap(0)), 0);
}

export function getGenericCombination({ channel, combinations, influence, inventory, variants }) {
  switch (channel) {
    case ETSY: {
      const genericCombination = {
        [ATTRIBUTE.VISIBILITY]: true,
      };

      if (inventory) {
        genericCombination[ATTRIBUTE.PRICE] = getSize(influence.get(ATTRIBUTE.PRICE)) > 0
          ? ATTRIBUTE_DEFAULTS[ETSY][ATTRIBUTE.PRICE]
          : inventory.getIn([ATTRIBUTE.PRICE, VALUE]);

        genericCombination[ATTRIBUTE.QUANTITY] = getSize(influence.get(ATTRIBUTE.QUANTITY)) > 0
          ? ATTRIBUTE_DEFAULTS[ETSY][ATTRIBUTE.QUANTITY]
          : inventory.getIn([ATTRIBUTE.QUANTITY, VALUE]);

        genericCombination[ATTRIBUTE.SKU] = getSize(influence.get(ATTRIBUTE.SKU)) > 0
          ? ATTRIBUTE_DEFAULTS[ETSY][ATTRIBUTE.SKU]
          : inventory.getIn([ATTRIBUTE.SKU, VALUE]);
      } else {
        genericCombination[ATTRIBUTE.PRICE] = null;
        genericCombination[ATTRIBUTE.QUANTITY] = null;
        genericCombination[ATTRIBUTE.SKU] = null;
      }

      return genericCombination;
    }

    case SHOPIFY: {
      const genericOffering = {
        imageId: null,
        imageHash: null,
      };

      if (inventory) {
        genericOffering[ATTRIBUTE.BARCODE] = (
          inventory.getIn([ATTRIBUTE.BARCODE, VALUE]) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.BARCODE]
        );

        genericOffering[ATTRIBUTE.CAP] = (
          inventory.getIn([ATTRIBUTE.CAP, VALUE]) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.CAP]
        );

        genericOffering[ATTRIBUTE.CHARGE_TAX] = inventory.hasIn([ATTRIBUTE.CHARGE_TAX, VALUE])
          ? inventory.getIn([ATTRIBUTE.CHARGE_TAX, VALUE])
          : ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.CHARGE_TAX];

        genericOffering[ATTRIBUTE.CONTINUE_SELLING] = inventory.hasIn([ATTRIBUTE.CONTINUE_SELLING, VALUE])
          ? inventory.getIn([ATTRIBUTE.CONTINUE_SELLING, VALUE])
          : ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.CONTINUE_SELLING];

        genericOffering[ATTRIBUTE.COUNTRY_CODE] = (
          inventory.getIn([ATTRIBUTE.COUNTRY_CODE, VALUE]) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.COUNTRY_CODE]
        );

        genericOffering[ATTRIBUTE.CPI] = (
          inventory.getIn([ATTRIBUTE.CPI, VALUE]) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.CPI]
        );

        genericOffering[ATTRIBUTE.HS_CODE] = (
          inventory.getIn([ATTRIBUTE.HS_CODE, VALUE]) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.HS_CODE]
        );

        genericOffering[ATTRIBUTE.PHYSICAL] = inventory.hasIn([ATTRIBUTE.CHARGE_TAX, VALUE])
          ? inventory.getIn([ATTRIBUTE.PHYSICAL, VALUE])
          : ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.PHYSICAL];

        genericOffering[ATTRIBUTE.PRICE] = (
          inventory.getIn([ATTRIBUTE.PRICE, VALUE]) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.PRICE]
        );

        genericOffering[ATTRIBUTE.QUANTITY] = (
          (
            getSize(variants) === 1 &&
            getSize(variants.getIn([0, 'options'])) === 1 &&
            inventory.getIn([ATTRIBUTE.QUANTITY, VALUE])
          ) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.QUANTITY]
        );

        genericOffering[ATTRIBUTE.SKU] = getSize(combinations)
          ? ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.SKU]
          : inventory.getIn([ATTRIBUTE.SKU, VALUE]) || ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.SKU];

        genericOffering[ATTRIBUTE.TRACK_QUANTITY] = inventory.hasIn([ATTRIBUTE.CHARGE_TAX, VALUE])
          ? inventory.getIn([ATTRIBUTE.TRACK_QUANTITY, VALUE])
          : ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.TRACK_QUANTITY];

        genericOffering[ATTRIBUTE.UNIT] = (
          inventory.getIn([ATTRIBUTE.UNIT, VALUE]) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.UNIT]
        );

        genericOffering[ATTRIBUTE.WEIGHT] = (
          inventory.getIn([ATTRIBUTE.WEIGHT, VALUE]) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.WEIGHT]
        );
      } else {
        genericOffering[ATTRIBUTE.BARCODE] = null;
        genericOffering[ATTRIBUTE.CAP] = null;
        genericOffering[ATTRIBUTE.CHARGE_TAX] = true;
        genericOffering[ATTRIBUTE.CONTINUE_SELLING] = null;
        genericOffering[ATTRIBUTE.COUNTRY_CODE] = null;
        genericOffering[ATTRIBUTE.CPI] = null;
        genericOffering[ATTRIBUTE.HS_CODE] = null;
        genericOffering[ATTRIBUTE.PHYSICAL] = true;
        genericOffering[ATTRIBUTE.PRICE] = null;
        genericOffering[ATTRIBUTE.QUANTITY] = null;
        genericOffering[ATTRIBUTE.SKU] = null;
        genericOffering[ATTRIBUTE.TRACK_QUANTITY] = false;
        genericOffering[ATTRIBUTE.UNIT] = WEIGHT_UNITS[0];
        genericOffering[ATTRIBUTE.WEIGHT] = null;
      }

      return genericOffering;
    }

    case VELA: {
      return inventory
        ? {
          [ATTRIBUTE.PRICE]: inventory.getIn([ATTRIBUTE.PRICE, VALUE]) || ATTRIBUTE_DEFAULTS[VELA][ATTRIBUTE.PRICE],
          [ATTRIBUTE.QUANTITY]: (
            (
              getSize(variants) === 1 &&
              getSize(variants.getIn([0, 'options'])) === 1 &&
              inventory.getIn([ATTRIBUTE.QUANTITY, VALUE])
            ) ||
            ATTRIBUTE_DEFAULTS[VELA][ATTRIBUTE.QUANTITY]
          ),
          [ATTRIBUTE.SKU]: getSize(combinations)
            ? ATTRIBUTE_DEFAULTS[VELA][ATTRIBUTE.SKU]
            : inventory.getIn([ATTRIBUTE.SKU, VALUE]) || ATTRIBUTE_DEFAULTS[VELA][ATTRIBUTE.SKU],
        }
        : {
          [ATTRIBUTE.PRICE]: null,
          [ATTRIBUTE.QUANTITY]: null,
          [ATTRIBUTE.SKU]: null,
        };
    }

    default: {
      return {};
    }
  }
}

export function findExistingCombination({ channel, combinations, config }) {
  function filterCombinations(combination) {
    function reduceProperties(all, property) {
      function findOption(option) {
        function reduceProperty(result, value, key) {
          return result && option.get(key) === value;
        }

        return reduce(property, true, reduceProperty);
      }

      return all && combination.get('options').find(findOption);
    }

    return config.options.reduce(reduceProperties, true);
  }

  function reduceVisible(visible, offering) {
    return visible || offering.get(ATTRIBUTE.VISIBILITY);
  }

  function findSameCombination(combination) {
    const options = combination.get('options');

    if (getSize(config.names) > getSize(options)) {
      config.names.splice(getSize(options) - getSize(config.names));
    }

    function reduceNames(result, name, index) {
      return result && (name ? options.getIn([index, 'name']) === name : result);
    }

    return config.names.reduce(reduceNames, true);
  }

  switch (channel) {
    case ETSY: {
      const allSuitableCombinations = combinations.filter(filterCombinations);

      return allSuitableCombinations.has(0)
        ? allSuitableCombinations
          .get(0)
          .set(ATTRIBUTE.VISIBILITY, allSuitableCombinations.reduce(reduceVisible, false))
        : undefined;
    }

    case VELA:
    case SHOPIFY: {
      return combinations.find(findSameCombination);
    }

    default: {
      return undefined;
    }
  }
}

export function findSuitableCombination({ combinations, optionId, variantId }) {
  function findCombination(combination) {
    function findOption(option) {
      return option.get('variantId') === variantId && option.get('optionId') === optionId;
    }

    return combination.get('options').find(findOption);
  }

  return combinations.find(findCombination);
}

export function getCombination({ channel, combinations, config, genericCombination }) {
  switch (channel) {
    case ETSY: {
      const existing = findExistingCombination({ channel, combinations, config });

      if (!existing) return genericCombination;

      return {
        [ATTRIBUTE.PRICE]: config.influence[ATTRIBUTE.PRICE]
          ? existing.get(ATTRIBUTE.PRICE)
          : genericCombination[ATTRIBUTE.PRICE],
        [ATTRIBUTE.QUANTITY]: config.influence[ATTRIBUTE.QUANTITY]
          ? existing.get(ATTRIBUTE.QUANTITY)
          : genericCombination[ATTRIBUTE.QUANTITY],
        [ATTRIBUTE.SKU]: config.influence[ATTRIBUTE.SKU]
          ? existing.get(ATTRIBUTE.SKU)
          : genericCombination[ATTRIBUTE.SKU],
        [ATTRIBUTE.VISIBILITY]: existing.get(ATTRIBUTE.VISIBILITY),
      };
    }

    case SHOPIFY: {
      const newOfferingNames = config.names.filter(isTruthy);
      const existing = findExistingCombination({ channel, combinations, config });

      if (!existing) return genericCombination;

      return {
        imageHash: existing.get('imageHash'),
        imageId: existing.get('imageId'),
        [DELETED]: existing.get(DELETED),
        [ATTRIBUTE.BARCODE]: existing.get(ATTRIBUTE.BARCODE),
        [ATTRIBUTE.CAP]: existing.get(ATTRIBUTE.CAP),
        [ATTRIBUTE.CHARGE_TAX]: existing.get(ATTRIBUTE.CHARGE_TAX),
        [ATTRIBUTE.CONTINUE_SELLING]: existing.get(ATTRIBUTE.CONTINUE_SELLING),
        [ATTRIBUTE.COUNTRY_CODE]: existing.get(ATTRIBUTE.COUNTRY_CODE),
        [ATTRIBUTE.CPI]: existing.get(ATTRIBUTE.CPI),
        [ATTRIBUTE.HS_CODE]: existing.get(ATTRIBUTE.HS_CODE),
        [ATTRIBUTE.PHYSICAL]: existing.get(ATTRIBUTE.PHYSICAL),
        [ATTRIBUTE.PRICE]: existing.get(ATTRIBUTE.PRICE),
        [ATTRIBUTE.QUANTITY]: existing.get(ATTRIBUTE.TRACK_QUANTITY)
          ? existing.get(ATTRIBUTE.QUANTITY)
          : genericCombination[ATTRIBUTE.QUANTITY],
        [ATTRIBUTE.SKU]: getSize(newOfferingNames) > getSize(existing.get('options'))
          ? genericCombination[ATTRIBUTE.SKU]
          : existing.get(ATTRIBUTE.SKU),
        [ATTRIBUTE.TRACK_QUANTITY]: existing.get(ATTRIBUTE.TRACK_QUANTITY),
        [ATTRIBUTE.UNIT]: existing.get(ATTRIBUTE.UNIT),
        [ATTRIBUTE.WEIGHT]: existing.get(ATTRIBUTE.WEIGHT),
      };
    }

    case VELA: {
      const newOfferingNames = config.names.filter(isTruthy);
      const existing = findExistingCombination({ channel, combinations, config });

      if (!existing) return genericCombination;

      return {
        [ATTRIBUTE.PRICE]: existing.get(ATTRIBUTE.PRICE),
        [ATTRIBUTE.QUANTITY]: existing.get(ATTRIBUTE.QUANTITY),
        [ATTRIBUTE.SKU]: getSize(newOfferingNames) > getSize(existing.get('options'))
          ? genericCombination[ATTRIBUTE.SKU]
          : existing.get(ATTRIBUTE.SKU),
      };
    }

    default: {
      return {};
    }
  }
}

export function updateCombinations({ channel, inventory, variants, variations }) {
  const combinations = variations.get(COMBINATIONS);
  let result = EMPTY.LIST;

  if (getSize(variants) === 0) return result;

  let id = 0;

  function findVariantById(variantId) {
    return function find(variant) {
      return variant.get('id') === variantId;
    };
  }

  switch (channel) {
    case ETSY: {
      const influence = variations.get(INFLUENCE);
      const genericCombination = getGenericCombination({ channel, influence, inventory });

      for (const option1 of variants.getIn([0, 'options'])) {
        const name1 = option1.get('value');
        const option1Id = option1.get('id');
        const variant1Id = variants.getIn([0, 'id']);
        const config = {
          influence: {
            [ATTRIBUTE.PRICE]: influence.getIn([ATTRIBUTE.PRICE, 0]),
            [ATTRIBUTE.QUANTITY]: influence.getIn([ATTRIBUTE.QUANTITY, 0]),
            [ATTRIBUTE.SKU]: influence.getIn([ATTRIBUTE.SKU, 0]),
          },
          options: [{ optionId: option1Id, variantId: variant1Id }],
        };

        if (getSize(variants.getIn([1, 'options'])) === 0) {
          id -= 1;

          const options = List([
            Map({ optionId: option1Id, variantId: variant1Id, name: name1 }),
          ]);

          result = result.push(
            Map({
              id,
              options,
              ...getCombination({ channel, combinations, config, genericCombination }),
            })
          );
        } else {
          for (const option2 of variants.getIn([1, 'options'])) {
            id -= 1;
            const name2 = option2.get('value');
            const option2Id = option2.get('id');
            const variant2Id = variants.getIn([1, 'id']);
            const options = List([
              Map({ optionId: option1Id, variantId: variant1Id, name: name1 }),
              Map({ optionId: option2Id, variantId: variant2Id, name: name2 }),
            ]);

            if (getSize(combinations.getIn([0, 'options'])) < getSize(variants)) {
              result = result.push(
                Map({
                  id,
                  options,
                  ...getCombination({ channel, combinations, config, genericCombination }),
                })
              );
            } else {
              let existing = findExistingCombination({
                channel,
                combinations,
                config: {
                  options: [
                    { optionId: option1Id, variantId: variant1Id },
                    { optionId: option2Id, variantId: variant2Id },
                  ],
                },
              });

              if (existing) {
                const existingId = existing.get('id');

                if (Number.isFinite(existingId) && existingId < 0) {
                  existing = existing.set('id', id);
                }

                result = result.push(existing);
              } else {
                if (influence.get(ALL)) {
                  result = result.push(Map({ id, options, ...genericCombination }));
                } else {
                  const { [ATTRIBUTE.VISIBILITY]: visibility } = genericCombination;
                  let {
                    [ATTRIBUTE.PRICE]: price,
                    [ATTRIBUTE.QUANTITY]: quantity,
                    [ATTRIBUTE.SKU]: sku,
                  } = genericCombination;

                  if (getSize(influence.get(ATTRIBUTE.PRICE)) > 0) {
                    const { optionId, variantId } = influence.getIn([ATTRIBUTE.PRICE, 0])
                      ? { optionId: option1Id, variantId: variant1Id }
                      : { optionId: option2Id, variantId: variant2Id };

                    const suitableCombination = findSuitableCombination({
                      combinations,
                      optionId,
                      variantId,
                    });

                    price = suitableCombination
                      ? suitableCombination.get(ATTRIBUTE.PRICE)
                      : genericCombination[ATTRIBUTE.PRICE];
                  }

                  if (getSize(influence.get(ATTRIBUTE.QUANTITY)) > 0) {
                    const { optionId, variantId } = influence.getIn([ATTRIBUTE.QUANTITY, 0])
                      ? { optionId: option1Id, variantId: variant1Id }
                      : { optionId: option2Id, variantId: variant2Id };

                    const suitableCombination = findSuitableCombination({
                      combinations,
                      optionId,
                      variantId,
                    });

                    quantity = suitableCombination
                      ? suitableCombination.get(ATTRIBUTE.QUANTITY)
                      : genericCombination[ATTRIBUTE.QUANTITY];
                  }

                  if (getSize(influence.get(ATTRIBUTE.SKU)) > 0) {
                    const { optionId, variantId } = influence.getIn([ATTRIBUTE.SKU, 0])
                      ? { optionId: option1Id, variantId: variant1Id }
                      : { optionId: option2Id, variantId: variant2Id };

                    const suitableCombination = findSuitableCombination({
                      combinations,
                      optionId,
                      variantId,
                    });

                    sku = suitableCombination
                      ? suitableCombination.get(ATTRIBUTE.SKU)
                      : genericCombination[ATTRIBUTE.SKU];
                  }

                  result = result.push(
                    Map({
                      id,
                      options,
                      [ATTRIBUTE.PRICE]: price,
                      [ATTRIBUTE.QUANTITY]: quantity,
                      [ATTRIBUTE.SKU]: sku,
                      [ATTRIBUTE.VISIBILITY]: visibility,
                    })
                  );
                }
              }
            }
          }
        }
      }

      return result;
    }

    case SHOPIFY: {
      const deleted = variations.get(DELETED, EMPTY.MAP);
      const genericCombination = getGenericCombination({ channel, combinations, inventory, variants });

      for (const option1 of variants.getIn([0, 'options'])) {
        const name1 = option1.get('value');
        const variant1Id = variants.getIn([0, 'id']);

        if (getSize(variants.getIn([1, 'options'])) === 0) {
          id -= 1;

          const options = List([
            Map({ name: name1 }),
          ]);

          const names = Array(2).fill(undefined);
          names[variations.get(VARIANTS).findIndex(findVariantById(variant1Id))] = name1;

          const combination = getCombination({ channel, combinations, config: { names }, genericCombination });

          if (
            combination === genericCombination &&
            isCombinationDeleted({ combination: Map({ options }), deleted })
          ) {
            continue;
          }

          result = result.push(Map({ id, options, ...combination }));
        } else {
          for (const option2 of variants.getIn([1, 'options'])) {
            const name2 = option2.get('value');
            const variant2Id = variants.getIn([1, 'id']);

            if (getSize(variants.getIn([2, 'options'])) === 0) {
              id -= 1;

              const options = List([
                Map({ name: name1 }),
                Map({ name: name2 }),
              ]);

              const names = Array(3).fill(undefined);
              names[variations.get(VARIANTS).findIndex(findVariantById(variant1Id))] = name1;
              names[variations.get(VARIANTS).findIndex(findVariantById(variant2Id))] = name2;

              const combination = getCombination({ channel, combinations, config: { names }, genericCombination });

              if (
                combination === genericCombination &&
                isCombinationDeleted({ combination: Map({ options }), deleted })
              ) {
                continue;
              }

              result = result.push(Map({ id, options, ...combination }));
            } else {
              for (const option3 of variants.getIn([2, 'options'])) {
                const name3 = option3.get('value');

                id -= 1;

                const names = [name1, name2, name3];
                const options = List([
                  Map({ name: name1 }),
                  Map({ name: name2 }),
                  Map({ name: name3 }),
                ]);

                const combination = getCombination({ channel, combinations, config: { names }, genericCombination });

                if (
                  combination === genericCombination &&
                  isCombinationDeleted({ combination: Map({ options }), deleted })
                ) {
                  continue;
                }

                result = result.push(Map({ id, options, ...combination }));
              }
            }
          }
        }
      }

      return result;
    }

    case VELA: {
      const genericCombination = getGenericCombination({ channel, combinations, inventory, variants });

      for (const option1 of variants.getIn([0, 'options'])) {
        const name1 = option1.get('value');
        const option1Id = option1.get('id');
        const variant1Id = variants.getIn([0, 'id']);

        if (getSize(variants.getIn([1, 'options'])) === 0) {
          id -= 1;

          const options = List([
            Map({ optionId: option1Id, variantId: variant1Id, name: name1 }),
          ]);

          const names = Array(2).fill(undefined);
          names[variations.get(VARIANTS).findIndex(findVariantById(variant1Id))] = name1;
          const combination = getCombination({ channel, combinations, config: { names }, genericCombination });
          result = result.push(Map({ id, options, ...combination }));
        } else {
          for (const option2 of variants.getIn([1, 'options'])) {
            const name2 = option2.get('value');
            const option2Id = option2.get('id');
            const variant2Id = variants.getIn([1, 'id']);

            if (getSize(variants.getIn([2, 'options'])) === 0) {
              id -= 1;

              const options = List([
                Map({ optionId: option1Id, variantId: variant1Id, name: name1 }),
                Map({ optionId: option2Id, variantId: variant2Id, name: name2 }),
              ]);

              const names = Array(3).fill(undefined);
              names[variations.get(VARIANTS).findIndex(findVariantById(variant1Id))] = name1;
              names[variations.get(VARIANTS).findIndex(findVariantById(variant2Id))] = name2;
              const combination = getCombination({ channel, combinations, config: { names }, genericCombination });
              result = result.push(Map({ id, options, ...combination }));
            } else {
              for (const option3 of variants.getIn([2, 'options'])) {
                const name3 = option3.get('value');
                const option3Id = option3.get('id');
                const variant3Id = variants.getIn([2, 'id']);

                id -= 1;

                const names = [name1, name2, name3];
                const options = List([
                  Map({ optionId: option1Id, variantId: variant1Id, name: name1 }),
                  Map({ optionId: option2Id, variantId: variant2Id, name: name2 }),
                  Map({ optionId: option3Id, variantId: variant3Id, name: name3 }),
                ]);

                const combination = getCombination({ channel, combinations, config: { names }, genericCombination });
                result = result.push(Map({ id, options, ...combination }));
              }
            }
          }
        }
      }

      return result;
    }

    default: {
      return result;
    }
  }
}

export function getOptionsFromCombinations({ combinations, variantIndex }) {
  function reduceCombinationsToOptionsAndIndexes(result, combination, combinationIndex) {
    function findSameVariantCombination(item) {
      return item.getIn(['options', 0, 'name']) === options.getIn([variantIndex, 'name']);
    }

    const options = combination.get('options');
    const optionIndex = result.options.findIndex(findSameVariantCombination);

    if (optionIndex === -1) {
      result.indexes = result.indexes.push(combinationIndex);
      result.options = result.options.push(combination.set('options', options.slice(variantIndex, variantIndex + 1)));
    } else if (combination.get(ATTRIBUTE.VISIBILITY) && !result.options.getIn([optionIndex, ATTRIBUTE.VISIBILITY])) {
      result.options = result.options.setIn([optionIndex, ATTRIBUTE.VISIBILITY], combination.get(ATTRIBUTE.VISIBILITY));
    }

    return result;
  }

  return combinations.reduce(reduceCombinationsToOptionsAndIndexes, { options: EMPTY.LIST, indexes: EMPTY.LIST });
}

export function setDefaultValuesToCombinations({ attribute, inventory, variations }) {
  function getDefaultValue() {
    if (!inventory) return null;

    switch (attribute) {
      case ATTRIBUTE.PRICE: {
        return getSize(variations.getIn([INFLUENCE, attribute])) > 0
          ? AMOUNT.ZERO_CENT
          : inventory.getIn([attribute, VALUE]);
      }

      case ATTRIBUTE.QUANTITY: {
        return getSize(variations.getIn([INFLUENCE, attribute])) > 0
          ? AMOUNT.ONE
          : inventory.getIn([attribute, VALUE]);
      }

      case ATTRIBUTE.SKU: {
        return getSize(variations.getIn([INFLUENCE, attribute])) > 0
          ? ''
          : inventory.getIn([attribute, VALUE]);
      }

      default: {
        return null;
      }
    }
  }

  function mapCombinations(combination) {
    return combination.set(attribute, getDefaultValue());
  }

  return variations.set(COMBINATIONS, variations.get(COMBINATIONS).map(mapCombinations));
}

export function updateCombinationsWithPreferredValues({ attribute, variantIndex, variations }) {
  const combinations = variations.get(COMBINATIONS);

  function reduceCombinations(result, combination) {
    const visibility = combination.get(ATTRIBUTE.VISIBILITY);
    const optionId = combination.getIn(['options', variantIndex, 'optionId']);

    if (result[optionId] === undefined || (visibility && !result[optionId][ATTRIBUTE.VISIBILITY])) {
      result[optionId] = {
        [VALUE]: combination.get(attribute),
        [ATTRIBUTE.VISIBILITY]: combination.get(ATTRIBUTE.VISIBILITY),
      };
    }

    return result;
  }

  const options = combinations.reduce(reduceCombinations, {});

  function mapCombinations(combination) {
    const optionId = combination.getIn(['options', variantIndex, 'optionId']);
    const { [VALUE]: value, [ATTRIBUTE.VISIBILITY]: visibility } = options[optionId];
    return combination.set(attribute, value).set(ATTRIBUTE.VISIBILITY, visibility);
  }

  return variations.set(COMBINATIONS, combinations.map(mapCombinations));
}
